import { CSSProperties, forwardRef, useMemo } from 'react'

import { Card as AntCard, CardProps as AntCardProps } from 'antd'
import styled, { css } from 'styled-components'

import { DozerLoading } from '../DozerLoading'

import { buildPassTheme } from 'src/styles/theme'

const CustomCard = styled(AntCard)<{ clickable }>`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: 0;

  ${({ clickable, theme }) =>
    clickable &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${theme.colour.neutral.surface};
      }
    `}

  & .ant-card-body {
    padding: 1rem;
  }

  & + & {
    margin-top: 1rem; // add margin between sibling cards
  }
`

const Title = styled.span`
  ${({ theme }) => theme.font.subheading01}
  color: ${({ theme }) => theme.colour.secondary.base};
  margin-top: -0.25rem;
  margin-bottom: 1rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`

const TitleInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`

export const cardStyles = ({
  bordered = true,
  shadow = false,
}): CSSProperties => ({
  border: bordered
    ? `1px solid ${buildPassTheme.colour.neutral.border}`
    : 'none',
  boxShadow: shadow ? '2px 4px 20px -10px #292B3C4D' : 'none',
})

type Props = AntCardProps & {
  shadow?: boolean
  bordered?: boolean
  clickable?: boolean
  ref?: React.Ref<HTMLDivElement>
}

export const Card: React.FC<Props> = forwardRef(
  (
    {
      title,
      extra,
      shadow,
      bordered = true,
      clickable,
      type,
      loading,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const styles = useMemo(() => {
      return cardStyles({ shadow, bordered })
    }, [bordered, shadow])

    if (!title || type == 'inner')
      return (
        <CustomCard
          ref={ref}
          clickable={clickable}
          bordered={bordered}
          style={{ ...styles, ...style }}
          type={type}
          title={title}
          {...rest}
        >
          <DozerLoading loading={loading} relative noBackground />
          {children}
        </CustomCard>
      )

    return (
      <CustomCard
        ref={ref}
        clickable={clickable}
        bordered={bordered}
        style={{ ...styles, ...style }}
        {...rest}
      >
        <Title>
          <TitleInner>{title}</TitleInner>
          {extra}
        </Title>
        <DozerLoading loading={loading} relative noBackground />
        {children}
      </CustomCard>
    )
  }
)

Card.displayName = 'Card'

export type CardProps = Props
